import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, ArrowLeft } from 'lucide-react';
import { supabase } from '../../supabase/supabaseClient';

export default function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage('');
    setError('');
    if (!email) {
      setError('Email is required.');
      return;
    }
    setIsProcessing(true);
    try {
      const redirectTo = `${window.location.origin}/reset-password`;
      const { error: resetError } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo,
      });
      if (resetError) {
        throw resetError;
      }
      setMessage('If an account exists for this email, a reset link has been sent. Please check your inbox.');
    } catch (err) {
      console.error('[ForgotPassword] reset error:', err);
      setError(err.message || 'Failed to send reset email. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <div className="p-6">
        <Link to="/login" className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors text-sm font-medium">
          <ArrowLeft className="w-4 h-4" />
          Back to login
        </Link>
      </div>

      <div className="flex-1 flex items-center justify-center p-4 pb-20">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-white mb-2">Reset your password</h1>
            <p className="text-slate-400 text-sm">
              Enter the email associated with your account and well send you a secure reset link.
            </p>
          </div>

          <div className="bg-surface p-8 rounded-2xl border border-white/10 shadow-2xl">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="email" className="text-sm font-medium text-slate-400 block mb-2">Email</label>
                <div className="relative">
                  <Mail className="w-5 h-5 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
                  <input
                    type="email"
                    id="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    required
                    className="w-full bg-black/20 border border-white/10 rounded-lg py-3 pl-10 pr-4 text-white focus:ring-1 focus:ring-primary focus:border-primary focus:outline-none transition-all"
                  />
                </div>
              </div>

              {error && (
                <div className="text-red-400 text-xs mb-2 text-center bg-red-500/10 p-2 rounded border border-red-500/20">{error}</div>
              )}
              {message && (
                <div className="text-green-400 text-xs mb-2 text-center bg-green-500/10 p-2 rounded border border-green-500/20">{message}</div>
              )}

              <button
                type="submit"
                disabled={isProcessing}
                className="w-full flex items-center justify-center gap-2 px-5 py-3 bg-primary text-white font-bold rounded-lg transition-all duration-300 hover:shadow-glow-blue hover:bg-primary-hover transform hover:-translate-y-0.5 disabled:opacity-60"
              >
                {isProcessing ? 'Sending link…' : 'Send reset link'}
              </button>
            </form>
          </div>

          <p className="text-center text-sm text-slate-500 mt-6">
            Remembered your password?{' '}
            <Link to="/login" className="font-bold text-primary hover:text-white hover:underline">
              Back to login
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
